import { forwardRef } from 'react';
import { cn, focusRing } from '../utils/cn';
import { Badge, type BadgeTone } from '../primitives/Badge';
import { Icon, type IconName } from '../primitives/Icon';
import { EmptyState } from './EmptyState';
import { formatDate } from '../utils/format';

export type EvidenceItemStatus = 'Available' | 'Restricted' | 'Unavailable' | 'Superseded';

export interface EvidenceItem {
  id: string;
  title: string;
  sourceLabel: string;
  date?: string | null;
  status: EvidenceItemStatus;
  url?: string | null;
  ownerName?: string | null;
  note?: string | null;
}

const statusMeta: Record<EvidenceItemStatus, { label: string; icon: IconName; tone: BadgeTone }> = {
  Available: { label: 'Available', icon: 'check-circle', tone: 'success' },
  Restricted: { label: 'Restricted', icon: 'lock', tone: 'neutral' },
  Unavailable: { label: 'Unavailable', icon: 'x-circle', tone: 'warning' },
  Superseded: { label: 'Superseded', icon: 'clock', tone: 'neutral' },
};

export interface EvidencePanelProps extends React.HTMLAttributes<HTMLDivElement> {
  items: EvidenceItem[];
  title?: string;
  onOpenItem?: (item: EvidenceItem) => void;
  emptyReason?: string;
  density?: 'comfortable' | 'compact';
}

/**
 * Evidence backing an assertion. `Restricted` items are still listed — the user
 * is told a document exists and that they may not open it, which is different
 * from the unauthorized page state where existence itself is concealed.
 */
export const EvidencePanel = forwardRef<HTMLDivElement, EvidencePanelProps>(function EvidencePanel(
  {
    items,
    title = 'Evidence',
    onOpenItem,
    emptyReason = 'No evidence has been attached. Assertions without evidence carry lower confidence.',
    density = 'comfortable',
    className,
    ...rest
  },
  ref,
) {
  if (items.length === 0) {
    return (
      <div ref={ref} className={className} {...rest}>
        <EmptyState size="sm" icon="file" title="No evidence attached" reason={emptyReason} />
      </div>
    );
  }

  return (
    <div ref={ref} className={cn('flex flex-col gap-2', className)} {...rest}>
      <h3 className="text-dense font-semibold uppercase tracking-eyebrow text-content-secondary">
        {title}
      </h3>
      <ul className="flex flex-col gap-1.5">
        {items.map((item) => {
          const meta = statusMeta[item.status];
          const openable = item.status === 'Available' && (item.url || onOpenItem);
          return (
            <li
              key={item.id}
              className={cn(
                'flex items-start justify-between gap-3 rounded-lg border border-line bg-surface',
                density === 'compact' ? 'px-2.5 py-2' : 'px-3 py-2.5',
              )}
            >
              <div className="flex min-w-0 items-start gap-2.5">
                <Icon name="file" size={15} className="mt-0.5 text-content-muted" />
                <div className="flex min-w-0 flex-col gap-0.5">
                  {openable ? (
                    <a
                      href={item.url ?? undefined}
                      onClick={
                        onOpenItem
                          ? (event) => {
                              if (!item.url) event.preventDefault();
                              onOpenItem(item);
                            }
                          : undefined
                      }
                      target={item.url ? '_blank' : undefined}
                      rel={item.url ? 'noreferrer' : undefined}
                      className={cn(
                        'inline-flex items-center gap-1 truncate rounded-sm text-dense-lg font-medium text-content-link hover:underline',
                        focusRing,
                      )}
                    >
                      {item.title}
                      {item.url ? <Icon name="external" size={12} /> : null}
                    </a>
                  ) : (
                    <span className="truncate text-dense-lg font-medium text-content">
                      {item.title}
                    </span>
                  )}
                  <span className="truncate text-dense text-content-muted">
                    {item.sourceLabel}
                    {item.date ? ` · ${formatDate(item.date)}` : ''}
                    {item.ownerName ? ` · ${item.ownerName}` : ''}
                  </span>
                  {item.note ? (
                    <span className="text-dense text-content-secondary">{item.note}</span>
                  ) : null}
                </div>
              </div>
              <Badge tone={meta.tone} icon={meta.icon}>
                {meta.label}
              </Badge>
            </li>
          );
        })}
      </ul>
    </div>
  );
});
